/**
 * Rilevatore di fischio (effetto Larsen) sul microfono locale. Modulo puro, senza Web Audio:
 * lavora sugli spettri in dB prodotti da `AnalyserNode.getFloatFrequencyData()` (finestra di
 * Blackman, |X[k]|/N, 20·log10), uno per campionamento, ciascuno con il suo timestamp in ms.
 *
 * Il Larsen è un tono quasi puro, stabile in frequenza, forte e persistente. Un fotogramma è
 * "tonale" quando il picco nella banda di ricerca (500 Hz – 5 kHz):
 *   1. supera un livello assoluto (`minPeakDb`): un fischio udibile è forte;
 *   2. sta molto sopra le bande vicine (`minProminenceDb`): media di potenza dei bin tra
 *      `guardBins` e `neighborBins` di distanza, esclusi i lobi principali della finestra;
 *   3. domina lo spettro (`minDominance`): la potenza del picco (±guardBins) è una frazione
 *      grande della potenza totale tra 80 Hz e 8 kHz. La voce ha molte armoniche con energia
 *      simile, quindi nessuna domina; il rumore è a larga banda;
 *   4. non ha "vicini" forti (`minSecondPeakDb`): nella voce l'armonica adiacente sta pochi dB
 *      sotto la più forte, nel Larsen il resto dello spettro è molto più basso.
 * Il fischio è rilevato quando i fotogrammi tonali restano sulla stessa frequenza (tolleranza
 * `max(freqToleranceBins bin, freqToleranceRel)` rispetto alla frequenza iniziale, così un
 * glissato fischiettato non viene "inseguito") per almeno `minDurationMs`, con buchi brevi
 * (`maxGapMs`) tollerati perché il Larsen oscilla un po' di ampiezza.
 *
 * Le soglie sono volutamente conservative: un falso positivo spegne il microfono.
 */

export interface HowlDetectorConfig {
  minHz: number;
  maxHz: number;
  /** Livello minimo del picco, nella scala dB di getFloatFrequencyData (seno di ampiezza A ≈ 20·log10(A) − 13,6 dB). */
  minPeakDb: number;
  /** Il picco deve superare di tanto la media (in potenza) delle bande vicine. */
  minProminenceDb: number;
  /** Frazione minima della potenza 80 Hz – 8 kHz concentrata nel picco (0..1). */
  minDominance: number;
  /**
   * Distacco minimo (dB) tra il picco e il bin più forte delle bande vicine. La voce ha sempre
   * un'armonica adiacente (a f0 di distanza, entro `neighborBins`) pochi dB sotto la più forte;
   * il Larsen no.
   */
  minSecondPeakDb: number;
  /** Durata minima del tono stabile. */
  minDurationMs: number;
  /** Buco massimo tollerato tra due fotogrammi tonali della stessa sequenza. */
  maxGapMs: number;
  /** Bin esclusi attorno al picco (lobo principale della finestra di Blackman: ±3 bin). */
  guardBins: number;
  /** Ampiezza (in bin, per lato) della zona "vicina" usata per la prominenza. */
  neighborBins: number;
  /** Tolleranza di frequenza rispetto all'inizio della sequenza: il massimo tra bin e frazione. */
  freqToleranceBins: number;
  freqToleranceRel: number;
}

/** Modalità altoparlanti: più sensibile (il rischio di Larsen è reale). */
export const HOWL_SPEAKERS: HowlDetectorConfig = {
  minHz: 500,
  maxHz: 5000,
  minPeakDb: -45,
  minProminenceDb: 18,
  minDominance: 0.5,
  minSecondPeakDb: 15,
  minDurationMs: 450,
  maxGapMs: 120,
  guardBins: 3,
  neighborBins: 24,
  freqToleranceBins: 1.5,
  freqToleranceRel: 0.015,
};

/** Modalità cuffie: meno sensibile (un Larsen è improbabile, si evitano falsi allarmi). */
export const HOWL_HEADPHONES: HowlDetectorConfig = {
  ...HOWL_SPEAKERS,
  minPeakDb: -38,
  minProminenceDb: 24,
  minDominance: 0.7,
  minSecondPeakDb: 20,
  minDurationMs: 900,
};

/** Banda su cui si misura la dominanza del picco. */
const DOMINANCE_MIN_HZ = 80;
const DOMINANCE_MAX_HZ = 8000;
/** Fotogrammi tonali minimi in una sequenza, oltre alla durata (protegge da timestamp radi). */
const MIN_HITS = 4;

export interface FrameAnalysis {
  /** Frequenza del picco (Hz), null se la banda è vuota/silenziosa. */
  peakHz: number | null;
  peakDb: number;
  prominenceDb: number;
  dominance: number;
  /** Picco meno il bin più forte delle bande vicine (dB). */
  secondPeakDb: number;
  tonal: boolean;
}

export interface HowlResult {
  detected: boolean;
  /** Frequenza del fischio (Hz) quando rilevato, altrimenti quella della sequenza in corso. */
  frequency: number | null;
  /** Da quanto dura la sequenza tonale in corso (ms). */
  runMs: number;
}

const dbToPower = (db: number): number => (Number.isFinite(db) ? Math.pow(10, db / 10) : 0);

/** Analizza un singolo spettro (dB per bin, `fftSize / 2` valori). */
export function analyzeFrame(magsDb: ArrayLike<number>, sampleRate: number, fftSize: number, cfg: HowlDetectorConfig): FrameAnalysis {
  const binHz = sampleRate / fftSize;
  const n = magsDb.length;
  const lo = Math.max(1, Math.ceil(cfg.minHz / binHz));
  const hi = Math.min(n - 1, Math.floor(cfg.maxHz / binHz));
  let peak = -1;
  let peakDb = -Infinity;
  for (let k = lo; k <= hi; k++) {
    const v = magsDb[k]!;
    if (v > peakDb) {
      peakDb = v;
      peak = k;
    }
  }
  if (peak < 0 || !Number.isFinite(peakDb)) return { peakHz: null, peakDb: -Infinity, prominenceDb: 0, dominance: 0, secondPeakDb: 0, tonal: false };

  // Prominenza: media di potenza delle bande vicine, escluso il lobo principale del picco.
  let nSum = 0;
  let nCount = 0;
  let nMax = -Infinity;
  for (let d = cfg.guardBins + 1; d <= cfg.neighborBins; d++) {
    for (const k of [peak - d, peak + d]) {
      if (k < 1 || k >= n) continue;
      nSum += dbToPower(magsDb[k]!);
      nCount++;
      if (magsDb[k]! > nMax) nMax = magsDb[k]!;
    }
  }
  const neighborMean = nCount > 0 ? nSum / nCount : 0;
  const peakPower = dbToPower(peakDb);
  const prominenceDb = neighborMean > 0 ? 10 * Math.log10(peakPower / neighborMean) : Infinity;

  // Dominanza: potenza del picco (±guardBins) / potenza totale della banda vocale.
  const dLo = Math.max(1, Math.ceil(DOMINANCE_MIN_HZ / binHz));
  const dHi = Math.min(n - 1, Math.floor(DOMINANCE_MAX_HZ / binHz));
  let total = 0;
  let inPeak = 0;
  for (let k = dLo; k <= dHi; k++) {
    const p = dbToPower(magsDb[k]!);
    total += p;
    if (Math.abs(k - peak) <= cfg.guardBins) inPeak += p;
  }
  const dominance = total > 0 ? inPeak / total : 0;

  const secondPeakDb = Number.isFinite(nMax) ? peakDb - nMax : Infinity;

  const tonal =
    peakDb >= cfg.minPeakDb &&
    prominenceDb >= cfg.minProminenceDb &&
    dominance >= cfg.minDominance &&
    secondPeakDb >= cfg.minSecondPeakDb;
  return { peakHz: peak * binHz, peakDb, prominenceDb, dominance, secondPeakDb, tonal };
}

interface Run {
  start: number;
  lastHit: number;
  startHz: number;
  hz: number;
  hits: number;
}

export class HowlDetector {
  private run: Run | null = null;

  constructor(
    private cfg: HowlDetectorConfig,
    private readonly sampleRate: number,
    private readonly fftSize: number,
  ) {}

  setConfig(cfg: HowlDetectorConfig): void {
    if (cfg === this.cfg) return;
    this.cfg = cfg;
    this.reset();
  }

  getConfig(): HowlDetectorConfig {
    return this.cfg;
  }

  reset(): void {
    this.run = null;
  }

  /** Un nuovo spettro al tempo `t` (ms). Dopo un rilevamento lo stato si azzera da solo. */
  push(magsDb: ArrayLike<number>, t: number): HowlResult {
    const a = analyzeFrame(magsDb, this.sampleRate, this.fftSize, this.cfg);
    const run = this.run;
    if (a.tonal && a.peakHz !== null) {
      const binHz = this.sampleRate / this.fftSize;
      const tol = Math.max(this.cfg.freqToleranceBins * binHz, this.cfg.freqToleranceRel * a.peakHz);
      if (run && t - run.lastHit <= this.cfg.maxGapMs && Math.abs(a.peakHz - run.startHz) <= tol) {
        run.lastHit = t;
        run.hz = a.peakHz;
        run.hits++;
      } else {
        this.run = { start: t, lastHit: t, startHz: a.peakHz, hz: a.peakHz, hits: 1 };
      }
    } else if (run && t - run.lastHit > this.cfg.maxGapMs) {
      this.run = null;
    }
    const cur = this.run;
    if (!cur) return { detected: false, frequency: null, runMs: 0 };
    const runMs = cur.lastHit - cur.start;
    if (runMs >= this.cfg.minDurationMs && cur.hits >= MIN_HITS) {
      this.run = null;
      return { detected: true, frequency: Math.round(cur.hz), runMs };
    }
    return { detected: false, frequency: Math.round(cur.hz), runMs };
  }
}
